// Headline metric block, rendered above every dimension chart in the
// expanded card.
//
// Renders: label / baseline value / arrow / reform value / direction-
// coloured delta. When isModified is false, only the baseline shows
// alongside an italic "baseline" tag.
//
// Per the policy-neutrality posture, the metric is a factual delta
// (e.g. "Gini: 0.282 → 0.275 (−2.5%)") — never an editorial framing.
// Stable hook for tests: data-testid="headline-metric".

import { deltaTextColour, type Dimension } from "../lib/dimensions";
import { formatPercentChange } from "../lib/formatters";
import { InfoIcon } from "./info_icon";

interface Props {
  dimension: Dimension;
  label: string;
  baseline: number;
  reform: number;
  isModified: boolean;
  /** Optional override for the value formatter — defaults to the
   *  dimension's `formatter`. Charts that show a non-dimension-typed
   *  value (e.g. percentage-point gap for Redistribution) supply
   *  their own. */
  formatter?: (value: number) => string;
  /** Optional explanation surfaced via a small info icon next to the
   *  label. Used when the metric's name isn't self-explanatory — e.g.
   *  "D10 − D1 effective-rate gap". Same UX affordance as the per-
   *  parameter (i) tooltips on instruments. */
  tooltip?: string;
  /** Render the signed net change as the hero instead of the
   *  baseline → reform → % triple. Used where the metric *is* a flow
   *  rather than a level — e.g. the budget waterfall, whose chart sums
   *  to this same net change, so the two must read identically. */
  deltaOnly?: boolean;
}

const NEUTRAL_COLOUR = "#9a98a3";

export function HeadlineMetric({
  dimension,
  label,
  baseline,
  reform,
  isModified,
  formatter,
  tooltip,
  deltaOnly = false,
}: Props): JSX.Element {
  const formatValue = formatter ?? dimension.formatter;
  const delta = reform - baseline;
  const showDelta = isModified && Math.abs(delta) > 1e-6;
  const deltaColour = showDelta
    ? deltaTextColour(delta, dimension.goodWhen)
    : NEUTRAL_COLOUR;
  // U+2212 minus to match the chart's signed labels.
  const signedDelta = `${delta > 0 ? "+" : "−"}${formatValue(Math.abs(delta))}`;

  return (
    <div
      className="beamm-headline-metric"
      data-testid="headline-metric"
    >
      <span className="beamm-headline-metric-label">{label}</span>
      {tooltip && <InfoIcon description={tooltip} label="Metric info" />}
      {deltaOnly ? (
        showDelta ? (
          <span
            className="beamm-headline-metric-reform"
            style={{ color: deltaColour }}
          >
            {signedDelta}
          </span>
        ) : (
          <span className="beamm-headline-metric-baseline-tag">baseline</span>
        )
      ) : (
        <>
          <span className="beamm-headline-metric-baseline">
            {formatValue(baseline)}
          </span>
          {showDelta ? (
            <>
              <span className="beamm-headline-metric-arrow" aria-hidden="true">
                →
              </span>
              <span className="beamm-headline-metric-reform">
                {formatValue(reform)}
              </span>
              <span
                className="beamm-headline-metric-delta"
                style={{ color: deltaColour }}
              >
                ({formatPercentChange(reform, baseline)})
              </span>
            </>
          ) : (
            <span className="beamm-headline-metric-baseline-tag">baseline</span>
          )}
        </>
      )}
    </div>
  );
}
